import React, { useState, forwardRef, useEffect, useImperativeHandle, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import styled from 'styled-components'
import { Input, Table, Button, Modal, notification, Spin, Select } from 'antd';
import UserForm from './UserForm';
import Drawer from '../../Drawer';
import { Icon } from 'dlweb-components';
import { showModal } from '../../../store/actions';
import ContextMenu from '../../ContextMenu';
import ReplacementUserModal from './ReplacementUserModal';
import { useTableSelectionState }  from '../../../hooks'
import _ from 'lodash';

const UserManager = forwardRef(({setMenuItems, showModal, rootScope, location, isAdmin}, ref) => {

  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gridData, setGridData] = useState([]);
  const [pagination, setPagination] = useState({total: 0});
  const [currentFilters, setCurrentFilters] = useState({});
  const [menuCoordinates, setMenuCoordinates] = useState({});
  const [showReplacementDialog, setShowReplacementDialog] = useState(false);
  const [usersInWorkflow, setUsersInWorkflow] = useState([]);
  const [replacementUser, setReplacementUser] = useState('');
  const [selectedUsers, setSelectedUsers] = useTableSelectionState(['id', 'inWorkflow'], []);

  const [adminSettings, setAdminSettings] = useState({});

  const defaultButtons = isAdmin ? [
    {
      id: 'new-sql-user',
      action: 'new-sql-user',
      data: 'new-sql-user-data',
      icon: 'user-plus',
      text: 'New SQL User'
    },
  ] : [];

  const [menuButtons, setMenuButtons] = useState(defaultButtons);

  let searchInput = null;

  const selectedUserIds = useMemo(() => {
    return selectedUsers.map(o => o.id)
  }, [selectedUsers])

  useEffect(() => {
    axios.post('/api/settings/read/dto/wt').then((res) => {
      setAdminSettings(res.data.data)
    })
  }, [setAdminSettings])

  useEffect(() => {
    if(adminSettings.activeDirectory?.enabled && isAdmin) {

      const adButton = {
        id: 'new-ad-user',
        action: 'new-ad-user',
        data: 'new-ad-user-data',
        icon: 'user-plus',
        text: 'New AD User'
      }

      setMenuButtons(mb => [...mb, adButton])
    }
  }, [adminSettings, isAdmin])

  const loadUserGrid = useCallback((options) => {
    setLoading(true);
    axios.post('/api/users/search/dto/grid', options).then(
      res => { 
        setGridData(res.data.data);
        setPagination(p => ({ ...p, total: res.data._meta.totalRecords }));
        setLoading(false)
      }
    ).catch(error => {
      console.error(error);
      alert('Error loading data.');
      setLoading(false);      
    });
  }, []);

  const refreshUsers = useCallback(() => {
    loadUserGrid(currentFilters)
  }, [loadUserGrid, currentFilters]);

  const postDelete = useCallback((replacement) => {

    const ids = _.filter(selectedUserIds, o => o !== 'cssadmin');

    axios.post('/api/users/delete', { ids: ids, replacementId: replacement }).then(res => {
      if(res.data.errors.length > 0) {
        Modal.error({content: res.data.errors[0]});
        refreshUsers();
      } else {
        refreshUsers();
        setVisible(false);
        setSelectedUsers([]);
        notification.success({ message: `User${selectedUsers.length > 1 ? 's' : ''} deleted successfully.` });
      }

      setShowReplacementDialog(false)
    });
  }, [refreshUsers, setSelectedUsers, selectedUserIds, selectedUsers])

  const onReplacementOk = () => {
    postDelete(replacementUser);
  }

  const deleteSelectedUsers = useCallback(() => {

    const continueDelete = () => {

      const uiw = _.filter(selectedUsers, o => o.id !== 'cssadmin' && o.inWorkflow);

      if(uiw.length > 0) {
        setUsersInWorkflow(uiw.map(o => o.id));
        setShowReplacementDialog(true);
        return;
      }
      else {
        postDelete();
      }
  
    }

    if(_.find(selectedUsers, { id: 'cssadmin' })) {
      Modal.info({
        title: 'Cannot delete cssadmin',
        content: `User 'cssadmin' is selected, but will not be deleted.`,
        onOk: continueDelete
      });
    }
    else {
      continueDelete();
    }
  }, [selectedUsers, postDelete])

  const openDeleteConfirmation = useCallback(() => {

    Modal.confirm({
      title: <>
                <Icon type='exclamation-circle' size="lg" style={{color: 'var(--warning-color)', marginRight: '10px'}}/>
                Delete the following users?
             </>,
      content: <div>
                  <ul style={{listStyle: 'none', margin: '20px 0', padding: 0, maxHeight: '200px', overflowY: 'auto'}}>
                    {selectedUsers.map((item) => (
                      <li key={item.id}>{item.id}</li>
                    ))}
                  </ul>
                </div>,
      onOk: deleteSelectedUsers
    });
  }, [selectedUsers, deleteSelectedUsers])

  useImperativeHandle(ref, () => ({
    onButtonClick(action) {
      switch (action) {
        case 'new-sql-user':
          
          const sqlOptions = {
            title: 'New SQL User',
            maskClosable: false,
            footer: null,
            width: '800px',
          };

          showModal('NEW_SQL_USER', sqlOptions, { onSuccess: refreshUsers });
        break;
        case 'new-ad-user':
          
          const adOptions = {
            title: 'New AD User',
            maskClosable: false,
            footer: null,
            width: '850px',
            style: {
              minWidth: '700px'
            }
          };

          showModal('NEW_AD_USER', adOptions, { adminSettings, onSuccess: refreshUsers });
        break;
        case 'delete-user':
          openDeleteConfirmation();
          break;
        default:
          break;
      }
    },
  }))

  const userMenu = useMemo(() => [[
    {
      id: "delete-user",
      action: "delete-user",
      data: "delete-user-data",
      icon: "user-slash",
      text: `Delete User${selectedUsers.length > 1 ? 's' : ''}`,
    },
  ]], [selectedUsers]);

  const cssadminSelected = useMemo(() => {
    return selectedUsers.length === 1 && selectedUsers[0]?.id === 'cssadmin';
  }, [selectedUsers])

  useEffect(() => {

    if(selectedUsers.length > 0) {
      setMenuItems(isAdmin && !cssadminSelected ? userMenu: []);
    }
    else {
      setVisible(false);
      setMenuItems(isAdmin ? menuButtons : []);
    }
  }, [selectedUsers, isAdmin, setMenuItems, menuButtons, userMenu, cssadminSelected])

  useEffect(() => {
    const filters = { limit: 50, skip: 0};

    setCurrentFilters(filters);
    loadUserGrid(filters);

    setMenuItems(menuButtons);

  }, [setMenuItems, loadUserGrid, menuButtons])

  const getColumnSearchProps = (columnTitle, dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={node => {
            searchInput = node;
          }}
          placeholder={`Filter ${columnTitle}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(dataIndex, selectedKeys, confirm)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(dataIndex, selectedKeys, confirm)}
          size="small"
          style={{ width: 90, marginRight: 8 }}
        >
          Apply
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.select());
      }
    },
  });

  const handleSearch = (dataIndex, selectedKeys, confirm) => {
    confirm();
  };

  const handleReset = clearFilters => {
    clearFilters();
  };

  const handleTableChange = (pagination, filters, sorter) => {

    const pager = { ...pagination };
    pager.current = pagination.current;
    setPagination(pager);
        
    const filterObject = {};

    _.forEach(filters, (value, field) => {
      if(value) filterObject[field] = value[0]
    })

    const cf = {
      limit: pagination.pageSize,
      skip: (pagination.current - 1) * pagination.pageSize,
      order: [{
        column: sorter.columnKey,
        direction: sorter.order === 'ascend' ? 0 : 1
      }],
      userSqlFilter: filterObject
    }

    setCurrentFilters(cf)
    loadUserGrid(cf);
  };

  const openDrawer = () => {
    setVisible(true);
  }

  const columns = [
    {
      key: 'ID',
      title: 'User ID',
      dataIndex: 'id',
      ellipsis: true,
      defaultSortOrder: 'ascend',
      sorter: true,
      render: (value) => <a onClick={() => openDrawer()}>{value}</a>,
      ...getColumnSearchProps('User ID', 'ID'),
    },
    {
      key: 'FirstName',
      title: 'First Name',
      dataIndex: 'firstName',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('First Name', 'FirstName'),
    },
    {
      key: 'LastName',
      title: 'Last Name',
      dataIndex: 'lastName',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('Last Name', 'LastName'),
    },
    {
      key: 'Department',
      title: 'Department',
      dataIndex: 'department',
      sorter: true,
      ellipsis: true,
      ...getColumnSearchProps('Department', 'Department'),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedUsers.map(o => o.id),
    onSelect: (selectedKey, selected) => {
      setSelectedUsers(selected, false, [selectedKey]);
      openDrawer();
    },
    onSelectMultiple: (selected, selectedRows) => {
      openDrawer();
      setSelectedUsers(selected, false, selectedRows);
    },
    onSelectAll: (selected, selectedRows) => {
      setSelectedUsers(selected, false, !selected ? gridData : selectedRows);
      openDrawer();
    }
  };

  const onRowClick = (event, record) => {
    if (event.target.className.indexOf('ant-checkbox') > -1) return;
    setSelectedUsers(true, true, [record])
  }

  const openContextMenu = (e, record) => {

    const isSelected = e.target.closest('tr').className.indexOf('ant-table-row-selected') > -1;

    e.preventDefault();

    if(!isAdmin) return;

    setSelectedUsers(true, !isSelected, [{id: record.id, inWorkflow: record.inWorkflow}]);
    
    let coordinates = { x: e.pageX - 50, y: e.pageY - 50 };

    setMenuCoordinates(coordinates);
  }

  const onClose = () => {
    setVisible(false);
    setSelectedUsers([]);
  }

  const closeReplacementUserDialog = () => {
    setShowReplacementDialog(false);
    setReplacementUser('');
  }

  return(
    <>
    <ReplacementUserModal
      onSelect={setReplacementUser}
      onCancel={closeReplacementUserDialog}
      visible={showReplacementDialog}
      usersInWorkflow={usersInWorkflow}
      userIds={selectedUserIds}
      onOk={onReplacementOk}
    />
    <FlexContainer>
      <TableContainer>
        <Table
          scroll={{scrollToFirstRowOnChange: true, y: 'calc(100vh - 242px)', x: '1000px'}}
          tableLayout="fixed"
          loading={loading}
          pagination={{
            total: pagination.total,
            //showSizeChanger: true,
            //pageSizeOptions: [25, 50, 100],
            // showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: 50,
            defaultCurrent: 1
          }}
          rowSelection={{
            type: 'checkbox',
            ...rowSelection,
          }}
          columns={columns}
          dataSource={gridData}
          rowKey="id"
          onChange={handleTableChange}
          onRow={(record, rowIndex) => {
            return {
              onClick: event => onRowClick(event, record),
              onContextMenu: event => openContextMenu(event, record)
            };
          }}
        />
        </TableContainer>
        <Drawer visible={visible} onClose={onClose}>
          <UserForm
            userIds={selectedUserIds}
            //userIds={selectedUsers.map(o => o.id)}
            rootScope={rootScope}
            location={location}
            isAdmin={isAdmin}
            refreshUsers={refreshUsers}
          />
        </Drawer>
    </FlexContainer>
    {isAdmin && !cssadminSelected &&
      <ContextMenu
        menuItems={userMenu}
        coordinates={menuCoordinates}
        handleClose={() => setMenuCoordinates({})}
        onClick={() => openDeleteConfirmation()}
      />
    }
  </>
  )
})

const FlexContainer = styled.div`
  display: flex;
`
const TableContainer = styled.div`
  flex: auto;
  min-width: 300px;
  .ant-table {
    border-bottom: 1px solid #dee0e3;
  }
  .ant-table-body,
  .ant-table-tbody,
  .ant-table-content,
  .ant-table-placeholder,
  .ant-table-expanded-row-fixed {
    overflow: auto !important;
    min-height: calc(100vh - 242px);
  }
  .ant-pagination {
    float: left;
    margin: 6px 0 0 15px;
  }
`
const mapStateToProps = state => {
  const { isAdmin } = state.user;
  return { isAdmin };
};

const mapDispatchToProps = { showModal };

export default connect(mapStateToProps, mapDispatchToProps, null, {forwardRef: true})(UserManager);